import { EthnicityRaceSyncService } from "providers/ethnicity-race-sync/ethnicity-race-sync.service";
import { ContactDetails } from "../contact/contactDetails";
import PhoneNumber from "../contact/phone-number";
import Diagnosis from "./diagnosis";
import LookupValue from "./lookupValue";

export class Patient {
  patientParentId: number = 0;
  age: number = 0;
  birthDate: Date = new Date();
  careType: string;
  doNotResuscitateStatus: LookupValue = new LookupValue();
  firstName: string = "";
  fullName: string = "";
  id: number = 0; //patientChartId
  patientId: number = 0;
  lastName: string = "";
  gender: LookupValue = new LookupValue();
  ethnicities: LookupValue = new LookupValue();
  hopeEthnicities: LookupValue = new LookupValue();
  hopeRaces: LookupValue = new LookupValue();
  firstLanguage: LookupValue = new LookupValue();
  maritalStatus: LookupValue = new LookupValue();
  religion: LookupValue = new LookupValue();
  religionOtherText: string = "";
  riskPriority: LookupValue = new LookupValue();
  awareOfReferral: LookupValue = new LookupValue();
  prefix: LookupValue = new LookupValue();
  suffix: LookupValue = new LookupValue();
  middleInitial: string = "";
  levelOfCare: string = "";
  nickName: string = "";
  emergencyPreparedness: string = "";
  veteran: boolean = null;
  ssnUnknown: boolean = null;
  homeAddress: ContactDetails = new ContactDetails();
  hospiceTransferDate: Date = new Date();
  previousHospiceAdmissionDate: Date = new Date();
  phoneNumber: PhoneNumber = new PhoneNumber();
  emailAddress: string = "";
  hasHospiceTransfer: boolean = false;
  hasFuneralHome: boolean = false;
  hasPharmacy: boolean = false;
  patientChartStatus: string;
  uniqueIdentifier: string;
  referralAddress: ContactDetails = new ContactDetails();
  diagnosis: Diagnosis[] = [];
  externalId: number;
  livingWill: boolean = null;
  skilledBed: boolean = null;
  daysRemaining: number = null;
  dischargedReason: string;
  roomNumber: string;
  teamName: string;
  tenantId: number;
  admittedDatetime: string = "";
  dischargedDatetime: string = "";

  constructor(
    age: number = 0,
    birthDate: Date = null,
    careType: string = "",
    doNotResuscitateStatus: LookupValue = new LookupValue(),
    ethnicities: LookupValue = new LookupValue(),
    hopeEthnicities: LookupValue = new LookupValue(),
    hopeRaces: LookupValue = new LookupValue(),
    firstLanguage: LookupValue = new LookupValue(),
    firstName: string = "",
    fullName: string = "",
    gender: LookupValue = new LookupValue(),
    id: number = 0,
    lastName: string = "",
    maritalStatus: LookupValue = new LookupValue(),
    middleInitial: string = "",
    nickName: string = "",
    emergencyPreparedness: string = "",
    religion: LookupValue = new LookupValue(),
    veteran: boolean = null,
    ssnUnknown: boolean = null,
    awareOfReferral: LookupValue = new LookupValue(),
    riskPriority: LookupValue = new LookupValue(),
    hospiceTransferDate: Date = null,
    previousHospiceAdmissionDate: Date = null,
    emailAddress: string = "",
    phoneNumber: PhoneNumber = new PhoneNumber(),
    homeAddress: ContactDetails = new ContactDetails(),
    referralAddress: ContactDetails = new ContactDetails(),
    prefix: LookupValue = new LookupValue(),
    suffix: LookupValue = new LookupValue(),
    levelOfCare: string = "",
    uniqueIdentifier: string = "",
    externalId: number = -1,
    livingWill: boolean = null,
    skilledBed: boolean = null,
    daysRemaining: number = null,
    roomNumber: string = ""
  ) {
    this.prefix = prefix;
    this.suffix = suffix;
    this.riskPriority = riskPriority;
    this.emergencyPreparedness = emergencyPreparedness;
    this.id = id;
    this.fullName = fullName;
    this.birthDate = birthDate;
    this.careType = careType;
    this.firstName = firstName;
    this.middleInitial = middleInitial;
    this.lastName = lastName;
    this.doNotResuscitateStatus = doNotResuscitateStatus;
    this.veteran = veteran;
    this.nickName = nickName;
    this.gender = gender;
    this.maritalStatus = maritalStatus;
    this.religion = religion;
    this.ethnicities = ethnicities;
    this.hopeEthnicities = hopeEthnicities;
    this.hopeRaces = hopeRaces;
    this.age = age;
    this.firstLanguage = firstLanguage;
    this.homeAddress = homeAddress;
    this.awareOfReferral = awareOfReferral;
    this.hospiceTransferDate = hospiceTransferDate;
    this.emailAddress = emailAddress;
    this.referralAddress = referralAddress;
    this.phoneNumber = phoneNumber;
    this.previousHospiceAdmissionDate = previousHospiceAdmissionDate;
    this.levelOfCare = levelOfCare;
    this.uniqueIdentifier = uniqueIdentifier;
    this.externalId = externalId;
    this.livingWill = livingWill;
    this.skilledBed = skilledBed;
    this.daysRemaining = daysRemaining;
    this.roomNumber = roomNumber;
  }

  hasAwareOfReferral = (): boolean => {
    return (
      this.awareOfReferral !== null &&
      this.awareOfReferral.id !== null &&
      this.awareOfReferral.id > 0
    );
  };

  static cloneFromForm = (
    clone: any,
    ethnicityRaceSyncService: EthnicityRaceSyncService
  ): Patient => {
    let p = new Patient();
    p.firstName = clone.firstName;
    p.lastName = clone.lastName;
    p.middleInitial = clone.middleInitial;
    p.careType = clone.careType;
    p.nickName = clone.nickName;
    p.suffix = new LookupValue(clone.suffix, "");
    p.religion = new LookupValue(clone.religion, "");
    p.emailAddress = clone.emailAddress;

    p.phoneNumber = new PhoneNumber();
    p.phoneNumber.number = clone.phone;

    p.gender = new LookupValue(clone.gender, "");
    p.firstLanguage = new LookupValue(clone.firstLanguage, "");
    p.maritalStatus = new LookupValue(clone.maritalStatus, "");
    p.riskPriority = new LookupValue(clone.riskPriority, "");
    p.prefix = new LookupValue(clone.prefix, "");
    p.doNotResuscitateStatus = new LookupValue(clone.codeStatus, "");
    p.ethnicities = new LookupValue(
      ethnicityRaceSyncService.calculateEthnicityOrRace(clone.ethnicity),
      ""
    );
    p.hopeEthnicities = new LookupValue(
      ethnicityRaceSyncService.calculateEthnicityOrRace(clone.hopeEthnicity),
      ""
    );
    p.hopeRaces = new LookupValue(
      ethnicityRaceSyncService.calculateEthnicityOrRace(clone.race),
      ""
    );
    p.birthDate = clone.dateOfBirth;
    if (!p.homeAddress) p.homeAddress = new ContactDetails();
    p.homeAddress.address1 = clone.address1;
    p.homeAddress.city = clone.city;
    p.homeAddress.state = clone.state;
    p.homeAddress.zip = clone.zip;
    p.homeAddress.zipExtension = clone.zipExtension;
    p.homeAddress.county = clone.county;
    p.uniqueIdentifier = clone.ssn;
    p.livingWill = clone.livingWill;
    p.skilledBed = clone.skilledBed;
    p.daysRemaining = clone.daysRemaining;
    p.roomNumber = clone.roomNumber;
    p.emergencyPreparedness = clone.emergencyPreparedness;

    return p;
  };

  static convertPatientToPatientChart = (p: Patient): any => {
    let patientChart = {
      id: p.id,
      careType: p.careType.toUpperCase(),
      riskPriority: p.riskPriority,
      doNotResuscitateStatus: p.doNotResuscitateStatus,
      livingWill: p.livingWill,
      skilledBed: p.skilledBed,
      daysRemaining: p.daysRemaining,
      roomNumber: p.roomNumber,
      referralAddress: p.referralAddress,
      awareOfReferral: p.awareOfReferral,
      patient: undefined,
    };

    let patient = {
      id: p.patientId ? p.patientId : undefined,
      firstName: p.firstName,
      lastName: p.lastName,
      middleInitial: p.middleInitial,
      nickName: p.nickName,
      suffix: p.suffix,
      prefix: p.prefix,
      gender: p.gender,
      birthDate: p.birthDate,
      maritalStatus: p.maritalStatus,
      firstLanguage: p.firstLanguage,
      ethnicities: p.ethnicities,
      hopeEthnicities: p.hopeEthnicities,
      hopeRaces: p.hopeRaces,
      religion: p.religion,
      religionOtherText: p.religionOtherText,
      emailAddress: p.emailAddress,
      phoneNumber: p.phoneNumber,
      homeAddress: p.homeAddress,
      veteran: p.veteran,
      uniqueIdentifier: p.uniqueIdentifier,
    };

    patientChart.patient = patient;

    return patientChart;
  };
  displayName = (): string => {
    let name = [];
    if (this.prefix) {
      name.push(this.prefix.description);
    }

    name.push(this.fullName);

    if (this.suffix) {
      name.push(this.suffix.description);
    }

    return name.join(" ");
  };

  displayUniqueIdentifier = (): string => {
    if (!this.uniqueIdentifier || this.uniqueIdentifier.length != 9)
      return this.uniqueIdentifier;
    return "***-**-" + this.uniqueIdentifier.substring(5, 9);
  };
}



// WEBPACK FOOTER //
// ./src/models/patient-chart/patient.ts